import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Alert, ProgressBar, Button, Spinner, Tab, Tabs } from 'react-bootstrap';
import { 
  FaRobot, FaFileAlt, FaExclamationTriangle, 
  FaClock, FaStickyNote, FaPencilAlt, FaInfoCircle,
  FaCheckCircle, FaTimesCircle, FaChartLine
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';

interface AIComplianceDisplayProps {
  transaction: any;
  onNavigateToDocuments?: () => void;
}

const AIComplianceDisplay: React.FC<AIComplianceDisplayProps> = ({ 
  transaction, 
  onNavigateToDocuments 
}) => {
  const [loading, setLoading] = useState(true);
  const [aiAnalyzedDocs, setAiAnalyzedDocs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    if (transaction?.id) {
      fetchAIAnalysis();
    }
  }, [transaction?.id]);

  const fetchAIAnalysis = async () => {
    try {
      setLoading(true);
      console.log('Fetching AI analysis for transaction:', transaction.id);
      
      // Fetch AI analyzed documents from the broker-review endpoint
      const response = await axiosInstance.get('/broker-review/ai-analyzed-documents');
      console.log('AI analyzed documents response:', response.data);
      
      if (response.data.success && response.data.documents) {
        // Get all documents that belong to this transaction
        const transactionDocIds = transaction.documents?.map((d: any) => d.id) || [];
        console.log('Transaction document IDs:', transactionDocIds);
        
        // Filter for this transaction's documents
        const relevantDocs = response.data.documents.filter((doc: any) => {
          // Check if this document belongs to our transaction
          return transactionDocIds.includes(doc.document_id) || 
                 transactionDocIds.includes(doc.id) ||
                 doc.transaction_id === transaction.id;
        });
        
        console.log('Relevant docs for transaction:', relevantDocs);
        
        // Process and enhance the documents with transaction info
        const processedDocs = relevantDocs.map((doc: any) => {
          const transDoc = transaction.documents?.find((d: any) => 
            d.id === doc.document_id || d.id === doc.id
          );
          
          return {
            ...doc,
            document_name: transDoc?.filename || doc.filename || doc.document_name || 'Unknown Document',
            document_type: transDoc?.type || transDoc?.document_type || doc.document_type || 'Unknown Type',
            uploaded_at: transDoc?.created_at || doc.created_at,
            ai_analysis: doc.ai_compliance_analysis
          };
        });
        
        // Sort by created date, newest first
        processedDocs.sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        setAiAnalyzedDocs(processedDocs);
      }
    } catch (error: any) {
      console.error('Error fetching AI analysis:', error);
      if (error.response?.status === 403) {
        console.error('Permission denied - agent may not have access to broker review endpoint');
      }
    } finally {
      setLoading(false);
    }
  };

  const getComplianceColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'danger';
  };

  const getRiskBadgeColor = (score: number) => {
    if (score === 0) return 'dark';
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'danger';
  };
  
  if (!transaction) {
    return (
      <Alert variant="warning">
        <FaClock className="me-2" />
        Loading transaction data...
      </Alert>
    );
  }
  
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" size="sm" />
        <p className="mt-3 text-muted">Analyzing compliance data...</p>
      </div>
    );
  }
  
  if (aiAnalyzedDocs.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <FaRobot className="text-muted" style={{ fontSize: '4rem', opacity: 0.3 }} />
          <h5 className="mt-3 text-muted">No AI Compliance Analysis Available</h5>
          <p className="text-muted">
            Documents need to be uploaded and analyzed before compliance data appears here.
          </p>
          <Button 
            variant="primary" 
            onClick={onNavigateToDocuments}
            className="mt-3"
          >
            <FaFileAlt className="me-2" />
            Go to Documents Tab
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // Calculate overall compliance metrics
  const overallScore = Math.round(
    aiAnalyzedDocs.reduce((sum, doc) => sum + (doc.ai_analysis?.compliance_score || 0), 0) / aiAnalyzedDocs.length
  );
  const criticalIssues = aiAnalyzedDocs.reduce((count, doc) => 
    count + (doc.ai_analysis?.issues?.filter((i: any) => i.severity === 'critical').length || 0), 0
  );
  const totalIssues = aiAnalyzedDocs.reduce((count, doc) => 
    count + (doc.ai_analysis?.issues?.length || 0), 0
  );

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <FaRobot className="me-2 text-primary" />
          AI Compliance Analysis
        </h4>
        <Badge bg="secondary">
          {aiAnalyzedDocs.length} Document{aiAnalyzedDocs.length !== 1 ? 's' : ''} Analyzed
        </Badge>
      </div>

      {/* Overall Metrics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Overall Compliance</h6>
              <div className="display-4 fw-bold" style={{ color: `var(--bs-${getComplianceColor(overallScore)})` }}>
                {overallScore}%
              </div>
              <ProgressBar 
                now={overallScore} 
                variant={getComplianceColor(overallScore)}
                style={{ height: '6px' }}
                className="mt-2"
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Risk Level</h6>
              <div className="py-3">
                <Badge 
                  bg={getRiskBadgeColor(overallScore)} 
                  className="fs-4 px-4 py-2"
                >
                  {overallScore === 0 ? 'CRITICAL' : 
                   overallScore >= 90 ? 'LOW' :
                   overallScore >= 70 ? 'MEDIUM' : 'HIGH'}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Critical Issues</h6>
              <div className="display-4 fw-bold text-danger">
                {criticalIssues}
              </div>
              <small className="text-muted">Requires immediate attention</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Issues</h6>
              <div className="display-4 fw-bold text-warning">
                {totalIssues}
              </div>
              <small className="text-muted">Across all documents</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Analysis Tabs */}
      <Card>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')} className="mb-3">
            <Tab eventKey="overview" title={<span><FaChartLine className="me-2" />Overview</span>}>
              <Row>
                {aiAnalyzedDocs.map((doc: any) => (
                  <Col lg={6} key={doc.id || doc.document_id} className="mb-3">
                    <Card className="h-100">
                      <Card.Header className={`bg-${getComplianceColor(doc.ai_analysis?.compliance_score || 0)} bg-opacity-10`}>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            <FaFileAlt className="me-2" />
                            {doc.document_name}
                          </h6>
                          <Badge bg={getComplianceColor(doc.ai_analysis?.compliance_score || 0)}>
                            {doc.ai_analysis?.compliance_score || 0}%
                          </Badge>
                        </div>
                        <small className="text-muted">{doc.document_type}</small>
                      </Card.Header>
                      <Card.Body>
                        {doc.ai_analysis ? (
                          <>
                            {/* Quick Stats */}
                            <Row className="mb-3 text-center">
                              <Col xs={4}>
                                <div className="border-end">
                                  <small className="text-muted d-block">Score</small>
                                  <strong className={`text-${getComplianceColor(doc.ai_analysis.compliance_score)}`}>
                                    {doc.ai_analysis.compliance_score}%
                                  </strong>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div className="border-end">
                                  <small className="text-muted d-block">Issues</small>
                                  <strong className="text-warning">
                                    {doc.ai_analysis.issues?.length || 0}
                                  </strong>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div>
                                  <small className="text-muted d-block">Status</small>
                                  {doc.review_status === 'approved' ? (
                                    <FaCheckCircle className="text-success" />
                                  ) : doc.review_status === 'needs_revision' ? (
                                    <FaExclamationTriangle className="text-warning" />
                                  ) : (
                                    <FaClock className="text-secondary" />
                                  )}
                                </div>
                              </Col>
                            </Row>

                            {/* Top Issues */}
                            {doc.ai_analysis.issues?.length > 0 && (
                              <div>
                                <small className="text-muted">Top Issues:</small>
                                <ul className="mb-0 mt-1">
                                  {doc.ai_analysis.issues.slice(0, 2).map((issue: any, idx: number) => (
                                    <li key={idx} className="small">
                                      <Badge bg={issue.severity === 'critical' ? 'danger' : 'warning'} className="me-1">
                                        {issue.severity}
                                      </Badge>
                                      {issue.message || issue.description}
                                    </li>
                                  ))}
                                  {doc.ai_analysis.issues.length > 2 && (
                                    <li className="small text-muted">
                                      ...and {doc.ai_analysis.issues.length - 2} more
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <FaClock size={24} />
                            <p className="mb-0 mt-2">Awaiting Analysis</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Tab>

            <Tab eventKey="details" title={<span><FaExclamationTriangle className="me-2" />Detailed Issues</span>}>
              {aiAnalyzedDocs.map((doc: any) => {
                const analysis = doc.ai_analysis;
                if (!analysis || !analysis.issues?.length) return null;

                return (
                  <div key={doc.id || doc.document_id} className="mb-4">
                    <h5 className="mb-3">
                      <FaFileAlt className="me-2" />
                      {doc.document_name}
                    </h5>
                    
                    {/* Signature Analysis */}
                    {analysis.field_analysis?.signature_details && (
                      <Card className="mb-3">
                        <Card.Header className="bg-light">
                          <h6 className="mb-0">Signature Analysis</h6>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={4}>
                              <small className="text-muted d-block">Signatures Found</small>
                              <strong>{analysis.field_analysis.signature_details.length}</strong>
                            </Col>
                            <Col md={4}>
                              {analysis.validation_summary?.invalid_signatures?.length > 0 && (
                                <>
                                  <small className="text-muted d-block">Invalid Signatures</small>
                                  <Badge bg="danger">
                                    {analysis.validation_summary.invalid_signatures.length}
                                  </Badge>
                                </>
                              )}
                            </Col>
                            <Col md={4}>
                              {analysis.field_analysis?.signatures_missing?.length > 0 && (
                                <>
                                  <small className="text-muted d-block">Missing Signatures</small>
                                  <Badge bg="warning">
                                    {analysis.field_analysis.signatures_missing.length}
                                  </Badge>
                                </>
                              )}
                            </Col>
                          </Row>

                          {/* Signature Details */}
                          {analysis.validation_summary?.invalid_signatures?.length > 0 && (
                            <Alert variant="danger" className="mt-3 mb-2">
                              <strong>Invalid Signatures:</strong> {analysis.validation_summary.invalid_signatures.join(', ')}
                              <br />
                              <small>These signers are not recognized as transaction parties</small>
                            </Alert>
                          )}

                          {analysis.field_analysis?.signatures_missing?.length > 0 && (
                            <Alert variant="warning" className="mt-2 mb-0">
                              <strong>Missing Signatures:</strong> {analysis.field_analysis.signatures_missing.join(', ')}
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    )}

                    {/* Issues List */}
                    <Card>
                      <Card.Header className="bg-light">
                        <h6 className="mb-0">
                          Compliance Issues ({analysis.issues.length})
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        {analysis.issues.map((issue: any, idx: number) => (
                          <Alert 
                            key={idx} 
                            variant={issue.severity === 'critical' ? 'danger' : 'warning'}
                            className="mb-2"
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <Badge bg={issue.severity === 'critical' ? 'danger' : 'warning'} className="me-2">
                                  {issue.severity}
                                </Badge>
                                <strong>{issue.type?.replace(/_/g, ' ').toUpperCase()}</strong>
                                <p className="mb-0 mt-1">{issue.message || issue.description}</p>
                                {issue.details?.signer && (
                                  <small className="text-muted">
                                    Signer: {issue.details.signer}
                                  </small>
                                )}
                              </div>
                            </div>
                          </Alert>
                        ))}
                      </Card.Body>
                    </Card>
                  </div>
                );
              })}
            </Tab>

            <Tab eventKey="recommendations" title={<span><FaPencilAlt className="me-2" />Recommendations</span>}>
              {aiAnalyzedDocs.map((doc: any) => {
                const analysis = doc.ai_analysis;
                if (!analysis?.suggestions?.length) return null;

                return (
                  <Card key={doc.id || doc.document_id} className="mb-3">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <FaFileAlt className="me-2" />
                        {doc.document_name}
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <ul className="mb-0">
                        {analysis.suggestions.map((suggestion: any, idx: number) => (
                          <li key={idx} className="mb-2">
                            {typeof suggestion === 'string' ? suggestion : suggestion.message}
                          </li>
                        ))}
                      </ul>
                      
                      {doc.review_status === 'needs_revision' && onNavigateToDocuments && (
                        <Button 
                          variant="warning" 
                          size="sm" 
                          className="mt-3"
                          onClick={onNavigateToDocuments}
                        >
                          <FaPencilAlt className="me-2" />
                          View Document & Make Corrections
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}

              {!aiAnalyzedDocs.some(doc => doc.ai_analysis?.suggestions?.length > 0) && (
                <Alert variant="success">
                  <FaCheckCircle className="me-2" />
                  No specific recommendations at this time. Documents appear to be in good compliance standing.
                </Alert>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Broker Notes Section */}
      {aiAnalyzedDocs.some(doc => doc.review_notes) && (
        <Card className="mt-3">
          <Card.Header className="bg-light">
            <h6 className="mb-0">
              <FaStickyNote className="me-2" />
              Broker Review Notes
            </h6>
          </Card.Header>
          <Card.Body>
            {aiAnalyzedDocs.filter(doc => doc.review_notes).map((doc: any) => (
              <Alert key={doc.id || doc.document_id} variant="info" className="mb-2">
                <strong>{doc.document_name}:</strong>
                <p className="mb-0 mt-1">{doc.review_notes}</p>
                <small className="text-muted">
                  Reviewed by: {doc.reviewed_by || 'Broker'} on {new Date(doc.reviewed_at || doc.created_at).toLocaleDateString()}
                </small>
              </Alert>
            ))}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AIComplianceDisplay;